/** The exact project-scoped tool name that creates a task card. */
export const CREATE_TASK_TOOL_NAME = "create_task";

/** The display and navigation fields needed for a task created by a tool. */
export interface CreatedTaskReference {
  id: string;
  projectId: string;
  title: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Extract the stable task reference returned by ProjectTools.create_task.
 *
 * The tool currently returns a JSON string containing the task DTO, while
 * live markers normalize JSON strings to objects before they reach the UI.
 * Supporting both shapes keeps a saved transcript and a live call on the
 * same render path. A future wrapper such as `{ task: {...} }` is also
 * accepted without trusting any fields other than the three we need.
 */
export function createdTaskReferenceFromResult(
  result: unknown,
): CreatedTaskReference | null {
  let value = result;

  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (!isRecord(value)) return null;
  const task = isRecord(value.task) ? value.task : value;
  const id = nonEmptyString(task.id);
  const projectId = nonEmptyString(task.project_id ?? task.projectId);
  if (!id || !projectId) return null;

  return {
    id,
    projectId,
    title: nonEmptyString(task.title),
  };
}
